import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Building2, Users, ChevronDown, ChevronRight, Plus, Settings } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PEOOrganizationService } from '@/services/peo-organization.service';
import { OrgChartNode } from '@/types/peo-organization.types';
import { ROLE_TYPE_LABELS } from '@/types/peo-organization.types';
import { TeamMemberDialog } from './TeamMemberDialog';
import { OrgPositionDialog } from './OrgPositionDialog';
import { RoleAssignmentDialog } from './RoleAssignmentDialog';

interface OrgChartBuilderProps {
  providerId: string;
  searchTerm: string;
  filterDepartment: string;
}

interface OrgNodeProps {
  node: OrgChartNode;
  level: number;
  isExpanded: boolean;
  onToggle: (nodeId: string) => void;
  onEdit: (nodeId: string) => void;
}

const OrgNode: React.FC<OrgNodeProps> = ({ 
  node, 
  level, 
  isExpanded, 
  onToggle, 
  onEdit 
}) => {
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="space-y-2">
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            {hasChildren && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggle(node.id)}
                className="p-1 h-6 w-6"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </Button>
            )}
            
            <Avatar className="w-10 h-10">
              <AvatarImage src="" />
              <AvatarFallback>{getInitials(node.name)}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <h4 className="font-medium truncate">{node.name}</h4>
              <p className="text-sm text-muted-foreground truncate">{node.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {node.title || 'No Role Assigned'}
                </Badge>
                {node.department && (
                  <Badge variant="secondary" className="text-xs">
                    {node.department}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              {node.is_manager && (
                <Badge variant="default" className="text-xs">
                  Manager
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(node.id)}
                className="h-8 w-8 p-0"
              >
                <Settings className="h-3 w-3" />
              </Button>
            </div>
          </div>
          
          {node.direct_reports_count > 0 && (
            <div className="mt-2 text-xs text-muted-foreground">
              {node.direct_reports_count} direct report{node.direct_reports_count !== 1 ? 's' : ''}
            </div>
          )}
        </CardContent>
      </Card>
      
      {hasChildren && isExpanded && (
        <div className="ml-8 border-l-2 border-muted pl-4 space-y-2">
          {node.children.map((child) => (
            <OrgNode
              key={child.id}
              node={child}
              level={level + 1}
              isExpanded={isExpanded}
              onToggle={onToggle}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const OrgChartBuilder: React.FC<OrgChartBuilderProps> = ({
  providerId,
  searchTerm,
  filterDepartment
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<OrgChartNode | null>(null);
  const [roleAssignmentOpen, setRoleAssignmentOpen] = useState(false);
  const [roleAssignmentUserId, setRoleAssignmentUserId] = useState<string>('');

  const { data: orgNodes, isLoading } = useQuery({
    queryKey: ['org-chart-nodes', providerId],
    queryFn: () => PEOOrganizationService.getOrgChartNodes(providerId),
    enabled: !!providerId
  });

  const handleToggle = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleEdit = (nodeId: string) => {
    const node = orgNodes?.find(n => n.id === nodeId);
    if (node) {
      setRoleAssignmentUserId(node.user_id);
      setRoleAssignmentOpen(true);
    }
  };

  const handleExpandAll = () => {
    if (!orgNodes) return;
    const allNodeIds = new Set<string>();
    
    const collectNodeIds = (nodes: OrgChartNode[]) => {
      nodes.forEach(node => {
        allNodeIds.add(node.id);
        if (node.children) {
          collectNodeIds(node.children);
        }
      });
    };
    
    collectNodeIds(orgNodes);
    setExpandedNodes(allNodeIds);
  };

  const handleCollapseAll = () => {
    setExpandedNodes(new Set());
  };

  const filteredNodes = orgNodes?.filter(node => {
    const matchesSearch = 
      node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.title.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = 
      filterDepartment === 'all' || node.department === filterDepartment;
    
    return matchesSearch && matchesDepartment;
  }) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Organization Chart</h2>
          <p className="text-muted-foreground">
            Visual representation of your team hierarchy and reporting structure.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExpandAll}>
            Expand All
          </Button>
          <Button variant="outline" onClick={handleCollapseAll}>
            Collapse All
          </Button>
          <OrgPositionDialog providerId={providerId}>
            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Position
            </Button>
          </OrgPositionDialog>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-full"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-4 bg-muted rounded w-32"></div>
                    <div className="h-3 bg-muted rounded w-24"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredNodes.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No organizational structure found</p>
            <p className="text-sm text-muted-foreground mt-2">
              Start by adding team members and defining their reporting relationships.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredNodes.map((node) => (
            <OrgNode
              key={node.id}
              node={node}
              level={0}
              isExpanded={expandedNodes.has(node.id)}
              onToggle={handleToggle}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      {!isLoading && filteredNodes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Organization Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold">{filteredNodes.length}</div>
                <div className="text-sm text-muted-foreground">Total Positions</div>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {filteredNodes.filter(n => n.is_manager).length}
                </div>
                <div className="text-sm text-muted-foreground">Managers</div>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {[...new Set(filteredNodes.map(n => n.department).filter(Boolean))].length}
                </div>
                <div className="text-sm text-muted-foreground">Departments</div>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {Math.max(...filteredNodes.map(n => n.level))}
                </div>
                <div className="text-sm text-muted-foreground">Hierarchy Levels</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <RoleAssignmentDialog 
        providerId={providerId}
        selectedUserId={roleAssignmentUserId}
        open={roleAssignmentOpen}
        onOpenChange={setRoleAssignmentOpen}
      >
        <div />
      </RoleAssignmentDialog>
    </div>
  );
};